import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider, useDispatch, useSelector, useStore } from 'react-redux';

import styles from './Robot.module.scss';

import actions from '@/actions';
import RobotMarker from '@/components/ui/map/RobotMarker';
import OlMap from '@/helpers/OlMap';

const cx = classNames.bind(styles);

const MarkerContent = ({ data: robot }) => {
  const showLabel = useSelector((state) => state.robotOptions[robot.id]?.label.visible);
  const robotStatus = useSelector((state) => state.robotStatus[robot.id]);
  const robotRef = useRef();
  const altitudeRef = useRef();

  useEffect(() => {
    if (!robotStatus) return;

    if (showLabel) {
      altitudeRef.current.innerText = robotStatus?.alt.toFixed(2);
    }
  }, [showLabel, robotStatus]);

  return (
    <div className={cx('container')}>
      <div ref={robotRef}>
        <RobotMarker data={robot} hideDirection />
      </div>
      {showLabel && (
        <div className={cx('panel')}>
          <div className={cx('wrapper')}>
            <div className={cx('info')}>
              <span ref={altitudeRef}>N/A</span>
              <span className={cx('unit')}>m</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Robot = ({ data: robot }) => {
  const dispatch = useDispatch();
  const robotStatus = useSelector((state) => state.robotStatus[robot.id]);
  const store = useStore();
  const element = useRef();

  useMountEffect(() => {
    element.current = getMarkerElement();

    element.current.addEventListener('click', (e) => {
      if (!robot.isOwned) return;

      // 수정자 키 조합 없이 단일 선택 시
      if (!e.ctrlKey && !e.metaKey) {
        const { targetRobotIds } = store.getState().robot;
        if (targetRobotIds.length === 1 && targetRobotIds[0] === robot.id) return;

        dispatch(actions.robot.reset());
      }

      dispatch(actions.robot.toggle(robot.id));
    });
    element.current.addEventListener('mouseenter', () => {
      dispatch(actions.callout.setText(robot.name));
    });
    element.current.addEventListener('mouseleave', () => {
      dispatch(actions.callout.setText(null));
    });

    return () => {
      OlMap.removeRobot(robot.id);
    };
  });

  useUpdateEffect(() => {
    const { lat, lon: lng } = robotStatus;

    OlMap.addRobot(robot.id, element.current, { lat, lng });
  }, [robotStatus]);

  const getMarkerElement = () => {
    const content = document.createElement('div');
    content.setAttribute('data-robot-id', robot.id);

    createRoot(content).render(
      <Provider store={store}>
        <MarkerContent data={robot} />
      </Provider>
    );
    return content;
  };
};

export default Robot;
